// Player/VisualStoryPlayer: MUI Box/styled → div + inline styles + CSS.
// Container-query responsive padding handled via @container rule.

import type { CustomFonts, PostJson, SlideOptionsSerialized, SlideSerialized } from "@scrolly/post-types";
import Renderer from "./Renderer";
import Credits from "./Credits";
import type { RefObject } from "react";
import { useSyncExternalStore } from "react";
import type VisualStoryStore from "./VisualStoryStore";
import { responsiveMultiplier } from "./calculateSlidePosition";
// viewerFont removed — the .slide-container CSS rule now inherits its
// font-family from the branding/custom font rather than hardcoding
// SofiaSansSemiCondensed, which caused a brief FOUC on re-renders.
import { getColorString } from "@scrolly/renderer";
import MarkdownViewer from "./MarkdownViewer";

interface Props {
    post: PostJson;
    showQuality: boolean;
    visualStoryStore: VisualStoryStore | null;
    canvasContainerRef: RefObject<HTMLDivElement | null>;
    htmlContainerRef: RefObject<HTMLDivElement | null>;
    mainContainerRef: RefObject<HTMLDivElement | null>;
    currentSlideIndex: number;
    fontFamilies: CustomFonts;
    /**
     * Plan-2 iOS-Fix: window/body-scroll-Mode. Dann pinnt der Renderer-Div via
     * position:fixed zum Viewport (statt position:sticky im Container) — der Body
     * scrollt, der Canvas bleibt im Bild. DEFAULT false (container, heute).
     */
    bodyScroll?: boolean;
}

const dummy = () => {
    return () => {};
};
const dummySnapShot = () => 800;

/**
 * Resolve the `.slide-content` textbox background. Handles three cases:
 *
 *   1. No color and no blur → undefined (fully transparent).
 *   2. No color but blur on → legacy white-translucent fallback so
 *      older PPI templates that rely on blur-only still render.
 *   3. Color set, blur off → solid `rgba(r,g,b,a)` from the color.
 *   4. Color set, blur on → same color BUT alpha capped at 0.3 so the
 *      backdrop-filter blur is visible behind the tint. Capping only
 *      kicks in when the authored alpha is higher than the cap — users
 *      who picked a subtler tint (e.g. 0.2) keep their exact choice.
 */
function tintBackgroundForBlur(
    color: { r: number; g: number; b: number; a: number } | null | undefined,
    blurOn: boolean,
): string | undefined {
    if (!color) {
        return blurOn ? "rgba(255,255,255,0.15)" : undefined;
    }
    const alpha = blurOn ? Math.min(color.a, 0.3) : color.a;
    return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
}

export default function VisualStoryPlayer({
    canvasContainerRef,
    htmlContainerRef,
    visualStoryStore,
    post,
    mainContainerRef,
    currentSlideIndex,
    fontFamilies,
    bodyScroll = false,
}: Props) {
    // isAfterStory-Subscription entfernt 2026-05-28 — der einzige Konsument
    // war der spacer-Toggle (Z. 242), der jetzt konstant stableHeight ist.
    // afterStorySubscribers im Store bleibt — könnte später wieder gebraucht
    // werden, kostet nichts.
    const slides = post.data.data.slides;

    const innerHeight = useSyncExternalStore<number>(
        (visualStoryStore && visualStoryStore.subscribeToHeightChanges) || dummy,
        (visualStoryStore && visualStoryStore.heightSnapshot) || dummySnapShot,
        (visualStoryStore && visualStoryStore.heightSnapshot) || dummySnapShot,
    );

    const innerWidth = useSyncExternalStore<number>(
        (visualStoryStore && visualStoryStore.subscribeToWidthChanges) || dummy,
        (visualStoryStore && visualStoryStore.widthSnapshot) || dummySnapShot,
        (visualStoryStore && visualStoryStore.widthSnapshot) || dummySnapShot,
    );

    const currentSlide: SlideSerialized | undefined = slides[currentSlideIndex];
    // A-4 (Code-Review 2026-05-29): optional-chain MUST continue through
    // `credits` — the SlideSerialized type declares it non-optional, but at
    // runtime any slide that bypasses the import adapter (legacy/hand-edited
    // DB row, future editor/Studio output) can lack the field entirely.
    // The previous `?.credits.light` stopped the chain at `currentSlide`, so
    // a credits-less slide threw and crashed the whole (shared) renderer tree
    // → white-screen. Line 87 already used the safe pattern; 85/86 did not.
    const creditsLight = currentSlide?.credits?.light;
    const creditsText = currentSlide?.credits?.text;
    const creditsPhotographerUrl = currentSlide?.credits?.photographerUrl;

    // Use innerHeight directly so the sticky renderer div updates on viewport changes.
    // innerHeight only changes on container resize (not during scroll), so no scroll-jump issues.
    const stableHeight = innerHeight;

    // Use the slide background color as the canvas background. The SVG
    // background rect is always stripped transparent at runtime, so this
    // CSS background provides the chart's "canvas color". Handles both
    // camelCase (editor-normalized) and snake_case (Studio raw JSON).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const presOpts = (post.data.data.presentationOptions ?? (post.data.data as any).presentation_options) as any;
    const slideOpts = presOpts?.slideOptions ?? presOpts?.slide_options;
    const bgc = slideOpts?.backgroundColor ?? slideOpts?.background_color;
    const scrollyBg: string = bgc && typeof bgc.r === 'number'
        ? `rgb(${bgc.r}, ${bgc.g}, ${bgc.b})`
        : "transparent";

    // Branded base font-size for the DOM slide-content typography.
    // Written by applyBrandingToPostJson from
    // branding.textStyles.body.fontSize → slideOptions.fontBaseSize.
    // If unset we fall back to the historical 18px.
    // Reuse the camelCase/snake_case-safe `slideOpts` resolved above instead of
    // re-dereferencing `presentationOptions.slideOptions` — the old direct deref
    // threw on a presentationOptions-less scrolly (same A-22 class as the
    // per-slide fallback further down).
    const rawFontBaseSize = (slideOpts as { fontBaseSize?: unknown } | undefined)?.fontBaseSize;
    const baseFontSize =
        typeof rawFontBaseSize === "number" && Number.isFinite(rawFontBaseSize) && rawFontBaseSize > 0
            ? rawFontBaseSize
            : 18;

    return (
        <>
            {/* Inject slide-content typography styles. Base font-size
                comes from applied branding (fontBaseSize, written by
                applyBrandingToPostJson) with a 18px fallback. All
                descendants use em/% so shrinking this rescales the
                whole slide-content block proportionally.

                BODY FONT OVERRIDE — Templates ship with baked `font-family`
                inline styles on <span> / <font> descendants, and — more
                importantly — many templates render body text inside
                <h5> elements (or h4/h6) instead of <p>. So the heading
                rule must be scoped to h1/h2/h3 only. h4..h6 get the
                body font with !important like everything else inside
                .slide-content. */}
            <style>{`
                ${(() => {
                    // Use the template's custom font if set, otherwise fall back
                    // to SofiaSansSemiCondensed (the platform's default renderer font).
                    // Multi-word font names MUST be quoted in CSS — without quotes
                    // "Open Sans" is parsed as two invalid generic families and the
                    // browser silently falls back to the default font.
                    const textFont = fontFamilies.textFontFamily
                        ? `'${fontFamilies.textFontFamily}', sans-serif`
                        : "'SofiaSansSemiCondensed', 'DM Sans', sans-serif";
                    // Set the SAME font on .slide-container (not the old
                    // font-shorthand which hardcoded SofiaSansSemiCondensed).
                    // This prevents a FOUC when re-renders briefly show the
                    // base font before !important rules kick in.
                    return `.slide-container {
                               position: relative;
                               z-index: 1000;
                               font-size: ${baseFontSize}px;
                               line-height: 1.4;
                               font-family: ${textFont};
                           }
                           .slide-content,
                           .slide-content *,
                           .slide-content h4,
                           .slide-content h4 *,
                           .slide-content h5,
                           .slide-content h5 *,
                           .slide-content h6,
                           .slide-content h6 * {
                               font-family: ${textFont} !important;
                           }`;
                })()}
                @container viewer-root-container (max-width: 850px) {
                    .slide-container { padding: 0 8px; }
                }
                .slide-content {
                    position: relative;
                    overflow-wrap: break-word;
                    hyphens: auto;
                }
                .slide-content h1, .slide-content h2, .slide-content h3,
                .slide-content h1 *, .slide-content h2 *, .slide-content h3 * {
                    ${fontFamilies.headlineFontFamily ? `font-family: '${fontFamilies.headlineFontFamily}', sans-serif !important;` : ""}
                    line-height: 1.2;
                }
                .slide-content h1 { font-size: 1.375em; }
                .slide-content h2 { font-size: 1.325em; }
                .slide-content h3 { font-size: 1.3em; line-height: 1.2; }
                .slide-content h4 { font-size: 1.275em; }
                .slide-content h5 { font-size: 1.25em; }
                .slide-content p {
                    margin: 0;
                }
                .slide-content a { text-decoration: underline; }
                .slide-content font[size="-3"] { font-size: 0.4em; }
                .slide-content font[size="-2"] { font-size: 0.6em; }
                .slide-content font[size="-1"] { font-size: 0.8em; }
                .slide-content font[size="+1"] { font-size: 1.2em; }
                .slide-content font[size="+2"] { font-size: 1.4em; }
                .slide-content font[size="+3"] { font-size: 1.6em; }
            `}</style>

            <div style={{ position: "relative", background: scrollyBg }} ref={mainContainerRef}>
                <div style={{ position: "relative", width: "100%" }} className="article-container">
                    <div
                        style={{
                            // Plan-2 body-scroll: fixed pinnt den Renderer zum
                            // Viewport (Body scrollt → Canvas bleibt im Bild).
                            // Container-Mode: sticky wie bisher.
                            position: bodyScroll ? "fixed" : "sticky",
                            zIndex: 998,
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: bodyScroll ? "auto" : 0,
                            height: bodyScroll ? "100lvh" : `${stableHeight}px`,
                            background: scrollyBg,
                        }}
                    >
                        <Renderer
                            canvasContainerRef={canvasContainerRef}
                            htmlContainerRef={htmlContainerRef}
                        />
                    </div>
                    {/* Credits footer — rendered as a SIBLING of the sticky
                        container (not as a child of Renderer) so it escapes
                        the sticky's z-index:998 stacking context. Nested
                        stacking contexts never let their inner z-index
                        escape, so the previous attempt (fixed inside the
                        sticky at zIndex 1200) was globally stuck behind
                        NavigationControls (fixed bottom-0 z-[1101]). At
                        this DOM level we share the same stacking context
                        as NavigationControls and can stack above it with
                        z-index 1200. */}
                    {creditsText && (
                        <div
                            style={{
                                position: "fixed",
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 1200,
                                pointerEvents: "none",
                            }}
                        >
                            <Credits
                                light={creditsLight}
                                text={creditsText}
                                photographerUrl={creditsPhotographerUrl}
                            />
                        </div>
                    )}
                    {/* Cover-Reservation-Spacer (2026-05-28): konstant stableHeight,
                        NICHT mehr `isAfterStory ? 0 : stableHeight`-toggle.

                        Hintergrund: vorher schaltete der spacer beim afterStory-Latch
                        (lastSlide passiert) auf 0 → article-container.scrollHeight
                        reduzierte sich um stableHeight → Browser clampt scrollTop →
                        sichtbarer Sprung nach oben am Story-Ende ("End-Snap",
                        Bene-Befund 2026-05-28).

                        Außerdem reservierte der spacer im Stage-Gating-Pattern
                        (ViewerChrome mountet ScrollyViewer erst nach Cover-Klick)
                        einen Cover-shaped leeren Bereich nach dem Cover-Ext-Mount,
                        was visuell wie ein "zweites Cover" wirkte
                        ("Cover-Doppelmount", gleicher Befund).

                        Fix: konstanter spacer = stableHeight. Layout-Distance bleibt
                        identisch über die ganze Story-Dauer. afterStory-Subscription
                        bleibt im Store (wird ggf. von anderer Logik konsumiert,
                        Master-Suche zeigt aber nur diese Stelle als Konsument). */}
                    <div style={{ height: stableHeight }} />
                    <div style={{ position: "relative", top: -stableHeight }}>
                        {slides.map((slide, i) => {
                            // A-22 (sister of A-4): guard the presentationOptions
                            // fallback so a slide without per-slide options on a
                            // scrolly without presentationOptions renders instead of
                            // crashing the shared renderer. Empty-object fallback is
                            // safe: the color helpers below already treat undefined
                            // backgroundColor/fontColor as "no color".
                            const options =
                                slide.options ||
                                post.data?.data?.presentationOptions?.slideOptions ||
                                ({} as SlideOptionsSerialized);
                            const previousSlide = slides[i - 1];
                            let animateAreaHeight = 0;
                            if (previousSlide) {
                                const animationDuration = Math.max(0, slide.from - previousSlide.from);
                                const factor = Math.max(animationDuration / 1000, 1);
                                animateAreaHeight = stableHeight / 2 + stableHeight * factor;
                            } else if (slide.from > 0) {
                                // First slide with from > 0: add initial spacer so the
                                // text doesn't appear before the user has scrolled past
                                // the start screen.
                                const factor = Math.max(slide.from / 1000, 1);
                                animateAreaHeight = stableHeight / 2 + stableHeight * factor;
                            }
                            const multiplier = responsiveMultiplier(innerWidth);
                            const showText = slide.text?.show ?? true;
                            const padding = options.padding * multiplier;
                            const borderRadius = options.borderRadius * multiplier;
                            const margin = options.margin * multiplier;
                            const companyBoxPosition = options.companyBoxPosition;
                            return (
                                <div
                                    key={slide.id}
                                    className="slide-container"
                                    style={{
                                        opacity: !slide.text || slide.text.text === "" ? 0 : 1,
                                        ...(fontFamilies.textFontFamily
                                            ? { fontFamily: fontFamilies.textFontFamily }
                                            : {}),
                                    }}
                                >
                                    <div
                                        className="slide-animate"
                                        style={{ height: animateAreaHeight, pointerEvents: "none" }}
                                    />
                                    {showText && slide.text?.text !== "" && (
                                        <div
                                            style={{
                                                padding: margin,
                                                display: "flex",
                                                justifyContent:
                                                    companyBoxPosition === "center"
                                                        ? "center"
                                                        : companyBoxPosition === "right"
                                                          ? "flex-end"
                                                          : "flex-start",
                                            }}
                                        >
                                            <div
                                                className="slide-content"
                                                style={{
                                                    // Background color resolution:
                                                    //  - No blur: solid color (from branding merge
                                                    //    or per-slide override)
                                                    //  - Blur on: same color, but alpha capped at
                                                    //    0.3 so the backdrop blur is visible behind
                                                    //    the tint. Without the cap an opaque color
                                                    //    would block the blur entirely and users
                                                    //    would think "Blur" is broken.
                                                    //  - No color at all + blur: legacy white-
                                                    //    translucent fallback for older PPI templates.
                                                    background: tintBackgroundForBlur(
                                                        options.backgroundColor,
                                                        slide.blurBackground === true,
                                                    ),
                                                    color: getColorString(options.fontColor) || undefined,
                                                    padding,
                                                    borderRadius,
                                                    // Apply the backdrop filter whenever blur is
                                                    // on — the alpha cap above guarantees the blur
                                                    // is visible through the tint.
                                                    ...(slide.blurBackground
                                                        ? { backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }
                                                        : {}),
                                                    ...(slide.boxShadow
                                                        ? { boxShadow: "0 8px 32px rgba(0,0,0,0.25)" }
                                                        : {}),
                                                }}
                                            >
                                                <MarkdownViewer source={slide.text?.text} />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </>
    );
}
